import React, { useCallback, useMemo } from 'react';
import date from 'date-and-time';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useGetPromoPerformanceQuery } from '../../../redux/api/fymAPI';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { PromoteContainer } from '../promote/PromoteContainer';
import { InsightsTabRow } from '../insights/InsightsTabRow';
import { insightsMessages } from '../insights/insightsMessages';
import { useInsightsCrumbs } from '../insights/useInsightsCrumbs';
import { smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { usePromotePosts } from '../promote/hooks/usePromotePosts';
import { usePostsPerDay } from './usePostsPerDay';
import { useSocialPeriod } from './useSocialPeriod';
import { PostsWentOutPanel } from './PostsWentOutPanel';
import { PromoFunnelPanel } from './PromoFunnelPanel';
import { SocialNumbersSection } from './SocialNumbersSection';
import { SocialPeriodControl } from './SocialPeriodControl';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialScreenProps {
  accountId: number;
  /** The post that `/social/post/:id` opens. */
  openPostId?: number;
}

/** The strip the hero draws. */
const BAR_DAYS = 20;
/** Wide enough that a post posted late still lands in the window. */
const FETCH_DAYS = 40;

/**
 * Insights → Social (the old Performance): the period picker with its dates, the totals per
 * channel and the posts table, then "Posts that went out" and the funnel (design social-connectors
 * b1, decision 3; your-intelligence g1). The head is the Insights head with its tabs (design
 * navigation-v2 b 2c). It has no connect control: setup is in Connections. Each block loads and
 * fails by itself.
 */
const SocialScreenComponent: React.FC<SocialScreenProps> = ({ accountId, openPostId }) => {
  const intl = useIntl();
  const crumbs = useInsightsCrumbs(smlNavigationMessages.social);
  const period = useSocialPeriod();
  const { data, isLoading, error, refetch } = useGetPromoPerformanceQuery({ accountId });

  const window = useMemo(() => {
    const now = new Date();
    return { from: date.addDays(now, -FETCH_DAYS).toISOString(), to: date.addDays(now, 1).toISOString() };
  }, []);

  const { posts } = usePromotePosts({ accountId, from: window.from, to: window.to });
  const perDay = usePostsPerDay(posts, BAR_DAYS);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return (
    <PromoteContainer
      title={intl.formatMessage(insightsMessages.title)}
      subtitle={intl.formatMessage(insightsMessages.subtitle)}
      crumbs={crumbs}
      tabs={<InsightsTabRow screen={SMLRouteNames.social} />}
      actions={
        <SocialPeriodControl days={period.days} historyDaysLimit={period.historyDaysLimit} onChange={period.setDays} />
      }
    >
      <SocialNumbersSection accountId={accountId} days={period.days} openPostId={openPostId} />

      {isLoading ? <LoadingView /> : null}
      {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
      {!isLoading && error === undefined ? (
        <div className={styles.hero}>
          <PostsWentOutPanel perDay={perDay} windowDays={BAR_DAYS} />
          <PromoFunnelPanel totals={data?.totals} />
        </div>
      ) : null}
    </PromoteContainer>
  );
};

export const SocialScreen = TypedMemo(SocialScreenComponent);
